/**
 * Servicio de Catálogos
 * Maneja operaciones CRUD para entidades de catálogo (Empresas, Bancos, Clientes, Cuentas, Usuarios)
 */
import api from './api';

// ============================================
// EMPRESAS
// ============================================

/**
 * Obtener todas las empresas
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Array>} Lista de empresas
 */
export const getEmpresas = async (filters = {}) => {
  try {
    const response = await api.get('/catalog/companies', { params: filters });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener empresas:', error);
    throw error;
  }
};

/**
 * Obtener una empresa por ID
 * @param {string} id - ID de la empresa
 * @returns {Promise<Object>} Datos de la empresa
 */
export const getEmpresaById = async (id) => {
  try {
    const response = await api.get(`/catalog/companies/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener empresa:', error);
    throw error;
  }
};

/**
 * Crear una nueva empresa
 * @param {Object} data - Datos de la empresa (nombre, ruc, etc.)
 * @returns {Promise<Object>} Empresa creada
 */
export const createEmpresa = async (data) => {
  try {
    const response = await api.post('/catalog/companies', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear empresa:', error);
    throw error;
  }
};

/**
 * Actualizar una empresa existente
 * @param {string} id - ID de la empresa
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Empresa actualizada
 */
export const updateEmpresa = async (id, data) => {
  try {
    const response = await api.put(`/catalog/companies/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar empresa:', error);
    throw error;
  }
};

/**
 * Eliminar una empresa
 * @param {string} id - ID de la empresa
 * @returns {Promise<void>}
 */
export const deleteEmpresa = async (id) => {
  try {
    const response = await api.delete(`/catalog/companies/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar empresa:', error);
    throw error;
  }
};

// ============================================
// BANCOS
// ============================================

/**
 * Obtener todos los bancos
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Array>} Lista de bancos
 */
export const getBancos = async (filters = {}) => {
  try {
    const response = await api.get('/catalog/banks', { params: filters });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener bancos:', error);
    throw error;
  }
};

/**
 * Obtener un banco por ID
 * @param {string} id - ID del banco
 * @returns {Promise<Object>} Datos del banco
 */
export const getBancoById = async (id) => {
  try {
    const response = await api.get(`/catalog/banks/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener banco:', error);
    throw error;
  }
};

/**
 * Crear un nuevo banco
 * @param {Object} data - Datos del banco (nombre, codigo, etc.)
 * @returns {Promise<Object>} Banco creado
 */
export const createBanco = async (data) => {
  try {
    const response = await api.post('/catalog/banks', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear banco:', error);
    throw error;
  }
};

/**
 * Actualizar un banco existente
 * @param {string} id - ID del banco
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Banco actualizado
 */
export const updateBanco = async (id, data) => {
  try {
    const response = await api.put(`/catalog/banks/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar banco:', error);
    throw error;
  }
};

/**
 * Eliminar un banco
 * @param {string} id - ID del banco
 * @returns {Promise<void>}
 */
export const deleteBanco = async (id) => {
  try {
    const response = await api.delete(`/catalog/banks/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar banco:', error);
    throw error;
  }
};

// ============================================
// CLIENTES
// ============================================

/**
 * Obtener todos los clientes
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Array>} Lista de clientes
 */
export const getClientes = async (filters = {}) => {
  try {
    const response = await api.get('/catalog/clients', { params: filters });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener clientes:', error);
    throw error;
  }
};

/**
 * Obtener un cliente por ID
 * @param {string} id - ID del cliente
 * @returns {Promise<Object>} Datos del cliente
 */
export const getClienteById = async (id) => {
  try {
    const response = await api.get(`/catalog/clients/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener cliente:', error);
    throw error;
  }
};

/**
 * Crear un nuevo cliente
 * @param {Object} data - Datos del cliente (razonSocial, cuit, etc.)
 * @returns {Promise<Object>} Cliente creado
 */
export const createCliente = async (data) => {
  try {
    const response = await api.post('/catalog/clients', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear cliente:', error);
    throw error;
  }
};

/**
 * Actualizar un cliente existente
 * @param {string} id - ID del cliente
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Cliente actualizado
 */
export const updateCliente = async (id, data) => {
  try {
    const response = await api.put(`/catalog/clients/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar cliente:', error);
    throw error;
  }
};

/**
 * Eliminar un cliente
 * @param {string} id - ID del cliente
 * @returns {Promise<void>}
 */
export const deleteCliente = async (id) => {
  try {
    const response = await api.delete(`/catalog/clients/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar cliente:', error);
    throw error;
  }
};

// ============================================
// CUENTAS BANCARIAS
// ============================================

/**
 * Obtener todas las cuentas bancarias
 * @param {Object} filters - Filtros opcionales (bancoId, activo)
 * @returns {Promise<Array>} Lista de cuentas bancarias
 */
export const getCuentasBancarias = async (filters = {}) => {
  try {
    const response = await api.get('/catalog/bank-accounts', { params: filters });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener cuentas bancarias:', error);
    throw error;
  }
};

/**
 * Obtener una cuenta bancaria por ID
 * @param {string} id - ID de la cuenta bancaria
 * @returns {Promise<Object>} Datos de la cuenta bancaria
 */
export const getCuentaBancariaById = async (id) => {
  try {
    const response = await api.get(`/catalog/bank-accounts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener cuenta bancaria:', error);
    throw error;
  }
};

/**
 * Crear una nueva cuenta bancaria
 * @param {Object} data - Datos de la cuenta (bancoId, numeroCuenta, titular, etc.)
 * @returns {Promise<Object>} Cuenta bancaria creada
 */
export const createCuentaBancaria = async (data) => {
  try {
    const response = await api.post('/catalog/bank-accounts', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear cuenta bancaria:', error);
    throw error;
  }
};

/**
 * Actualizar una cuenta bancaria existente
 * @param {string} id - ID de la cuenta bancaria
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Cuenta bancaria actualizada
 */
export const updateCuentaBancaria = async (id, data) => {
  try {
    const response = await api.put(`/catalog/bank-accounts/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar cuenta bancaria:', error);
    throw error;
  }
};

/**
 * Eliminar una cuenta bancaria
 * @param {string} id - ID de la cuenta bancaria
 * @returns {Promise<void>}
 */
export const deleteCuentaBancaria = async (id) => {
  try {
    const response = await api.delete(`/catalog/bank-accounts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar cuenta bancaria:', error);
    throw error;
  }
};

/**
 * Obtener cuentas bancarias por banco
 * @param {string} bancoId - ID del banco
 * @returns {Promise<Array>} Lista de cuentas del banco
 */
export const getCuentasByBanco = async (bancoId) => {
  try {
    const response = await api.get('/catalog/bank-accounts', {
      params: { bancoId },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener cuentas por banco:', error);
    throw error;
  }
};

// ============================================
// USUARIOS
// ============================================

/**
 * Obtener todos los usuarios
 * @param {Object} filters - Filtros opcionales (rol, activo)
 * @returns {Promise<Array>} Lista de usuarios
 */
export const getUsuarios = async (filters = {}) => {
  try {
    const response = await api.get('/catalog/users', { params: filters });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error al obtener usuarios:', error);
    throw error;
  }
};

/**
 * Obtener un usuario por ID
 * @param {string} id - ID del usuario
 * @returns {Promise<Object>} Datos del usuario
 */
export const getUsuarioById = async (id) => {
  try {
    const response = await api.get(`/catalog/users/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener usuario:', error);
    throw error;
  }
};

/**
 * Crear un nuevo usuario
 * @param {Object} data - Datos del usuario (nombre, email, password, rol, etc.)
 * @returns {Promise<Object>} Usuario creado
 */
export const createUsuario = async (data) => {
  try {
    const response = await api.post('/catalog/users', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear usuario:', error);
    throw error;
  }
};

/**
 * Actualizar un usuario existente
 * @param {string} id - ID del usuario
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Usuario actualizado
 */
export const updateUsuario = async (id, data) => {
  try {
    const response = await api.put(`/catalog/users/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar usuario:', error);
    throw error;
  }
};

/**
 * Restablecer la contraseña de un usuario de la empresa (solo administrador)
 * @param {string} id - ID del usuario
 * @param {string} password - Nueva contraseña
 * @returns {Promise<Object>} Respuesta del restablecimiento
 */
export const resetPasswordUsuario = async (id, password) => {
  try {
    const response = await api.patch(`/catalog/users/${id}/password`, { password });
    return response.data;
  } catch (error) {
    console.error('Error al restablecer contraseña:', error);
    throw error;
  }
};

/**
 * Eliminar un usuario
 * @param {string} id - ID del usuario
 * @returns {Promise<void>}
 */
export const deleteUsuario = async (id) => {
  try {
    const response = await api.delete(`/catalog/users/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar usuario:', error);
    throw error;
  }
};

// NOTA: Las siguientes funciones están comentadas porque no existen en el backend aún
// Si se necesitan, deben implementarse primero en backend/src/controllers/catalogoController.js
// y agregarse las rutas correspondientes en backend/src/routes/catalogoRoutes.js

/**
 * Cambiar contraseña de un usuario
 * @param {string} id - ID del usuario
 * @param {Object} data - Datos (passwordActual, passwordNueva)
 * @returns {Promise<Object>} Respuesta de cambio de contraseña
 */
// export const changePassword = async (id, data) => {
//   try {
//     const response = await api.patch(`/catalog/users/${id}/cambiar-password`, data);
//     return response.data;
//   } catch (error) {
//     console.error('Error al cambiar contraseña:', error);
//     throw error;
//   }
// };

/**
 * Activar/Desactivar un usuario
 * @param {string} id - ID del usuario
 * @param {boolean} activo - Estado activo
 * @returns {Promise<Object>} Usuario actualizado
 */
// export const toggleUsuarioStatus = async (id, activo) => {
//   try {
//     const response = await api.patch(`/catalog/users/${id}/estado`, { activo });
//     return response.data;
//   } catch (error) {
//     console.error('Error al cambiar estado del usuario:', error);
//     throw error;
//   }
// };

export default {
  // Empresas
  getEmpresas,
  getEmpresaById,
  createEmpresa,
  updateEmpresa,
  deleteEmpresa,
  // Bancos
  getBancos,
  getBancoById,
  createBanco,
  updateBanco,
  deleteBanco,
  // Clientes
  getClientes,
  getClienteById,
  createCliente,
  updateCliente,
  deleteCliente,
  // Cuentas Bancarias
  getCuentasBancarias,
  getCuentaBancariaById,
  createCuentaBancaria,
  updateCuentaBancaria,
  deleteCuentaBancaria,
  getCuentasByBanco,
  // Usuarios
  getUsuarios,
  getUsuarioById,
  createUsuario,
  updateUsuario,
  resetPasswordUsuario,
  deleteUsuario,
  // changePassword,        // Comentado - no implementado en backend
  // toggleUsuarioStatus,   // Comentado - no implementado en backend
};
